/**
 * @file ContactPopover.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-17
 *
 * Lightweight popover for showing contact details (phone, email)
 * when clicking on customer/haulier/rep names in dashboard widgets.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import './ContactPopover.css';

function ContactPopover({ name, type, anchorEl, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const popoverRef = useRef(null);

  // Fetch contact data
  useEffect(() => {
    if (!name || !type) return;
    setLoading(true);
    fetch(`${API_URL}/api/dashboard/contact-lookup?name=${encodeURIComponent(name)}&type=${type}`, {
      credentials: 'include'
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setData({ found: false }); setLoading(false); });
  }, [name, type]);

  // Close on click outside
  const handleClickOutside = useCallback((e) => {
    if (popoverRef.current && !popoverRef.current.contains(e.target)) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  // Position near anchor
  const getPosition = () => {
    if (!anchorEl) return {};
    const rect = anchorEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceRight = window.innerWidth - rect.left;

    const style = { position: 'fixed', zIndex: 10000 };

    if (spaceBelow > 200) {
      style.top = rect.bottom + 4;
    } else {
      style.bottom = window.innerHeight - rect.top + 4;
    }

    if (spaceRight > 300) {
      style.left = rect.left;
    } else {
      style.right = window.innerWidth - rect.right;
    }

    return style;
  };

  const formatPhone = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/[^\d+]/g, '');
    return cleaned;
  };

  const renderPhone = (label, phone) => {
    if (!phone) return null;
    return (
      <div className="popover-contact-row">
        <span className="material-symbols-rounded popover-row-icon">call</span>
        <div className="popover-row-content">
          <span className="popover-row-label">{label}</span>
          <a href={`tel:${formatPhone(phone)}`} className="popover-link phone-link">{phone}</a>
        </div>
      </div>
    );
  };

  const renderEmail = (email, label) => {
    if (!email) return null;
    return (
      <div className="popover-contact-row">
        <span className="material-symbols-rounded popover-row-icon">mail</span>
        <div className="popover-row-content">
          {label && <span className="popover-row-label">{label}</span>}
          <a href={`mailto:${email}`} className="popover-link email-link">{email}</a>
        </div>
      </div>
    );
  };

  return (
    <div className="contact-popover" ref={popoverRef} style={getPosition()}>
      {loading ? (
        <div className="popover-loading">
          <span className="material-symbols-rounded spinning">sync</span>
        </div>
      ) : !data?.found ? (
        <div className="popover-empty">
          <span className="material-symbols-rounded">person_off</span>
          <span>No contact info found for "{name}"</span>
        </div>
      ) : (
        <>
          <div className="popover-header">
            <span className="material-symbols-rounded popover-type-icon">
              {type === 'customer' ? 'person' : type === 'haulier' ? 'local_shipping' : 'badge'}
            </span>
            <div>
              <div className="popover-name">{data.name}</div>
              <div className="popover-type">{type}</div>
            </div>
            <button className="popover-close" onClick={onClose}>
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>

          <div className="popover-body">
            {/* Account-level phone/email */}
            {renderPhone('Phone', data.phone)}
            {renderPhone('Mobile', data.mobile)}
            {renderEmail(data.email)}

            {/* Contact persons (customers only) */}
            {data.contactPersons && data.contactPersons.length > 0 && (
              <div className="popover-persons">
                <div className="popover-persons-label">Contact People</div>
                {data.contactPersons.map((person, idx) => (
                  <div key={idx} className="popover-person">
                    <div className="popover-person-name">
                      <span className="material-symbols-rounded" style={{ fontSize: 14 }}>person</span>
                      {person.name || 'Unnamed'}
                    </div>
                    {person.email && (
                      <a href={`mailto:${person.email}`} className="popover-link email-link person-email">
                        {person.email}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* If nothing to show at all */}
            {!data.phone && !data.mobile && !data.email &&
             (!data.contactPersons || data.contactPersons.length === 0) && (
              <div className="popover-empty-detail">No phone or email on file</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ContactPopover;
